/**
 * useFilterBreakpoint Hook
 *
 * Manages filter visibility based on viewport breakpoints.
 * - Desktop (>=992px): Filters are always visible
 * - Mobile (<992px): Filters are collapsed by default
 *
 * @param {number} [breakpoint=992] - The breakpoint width in pixels (default: 992px for lg breakpoint)
 * @returns {{showFilters: boolean, setShowFilters: Function}} Filter visibility state and setter
 *
 * @example
 * ```jsx
 * const { showFilters, setShowFilters } = useFilterBreakpoint();
 *
 * return (
 *   <div>
 *     <button onClick={() => setShowFilters(v => !v)}>Toggle Filters</button>
 *     {showFilters && <FilterComponent />}
 *   </div>
 * );
 * ```
 */

import { useState, useEffect } from 'react';
import '../assets/scss/FilterResponsive.scss';

const useFilterBreakpoint = (breakpoint = 992) => {
  const [showFilters, setShowFilters] = useState(true);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= breakpoint) {
        setShowFilters(true); // desktop always open
      } else {
        setShowFilters(false); // mobile collapsed
      }
    };

    // Initialize on mount
    handleResize();

    // Listen to resize events
    window.addEventListener('resize', handleResize);

    // Cleanup listener on unmount
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);

  return { showFilters, setShowFilters };
};

export default useFilterBreakpoint;
